const PrivacyPolicy = () => {
   const pageStyle = {
     fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
     color: "#1f2937",
     background: "#f9fafb",
     minHeight: "100vh"
   }

   const headerStyle = {
     background: "linear-gradient(135deg, #111827 0%, #1e3a5f 100%)",
     color: "#fff",
     padding: "48px 24px",
     textAlign: "center"
   }

   const containerStyle = {
     maxWidth: "800px",
     margin: "-32px auto 48px",
     background: "#fff",
     borderRadius: "16px",
     boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
     padding: "40px 32px"
   }

   const highlightStyle = {
     background: "#f0f9ff",
     borderLeft: "4px solid #3b82f6",
     padding: "16px 20px",
     borderRadius: "0 8px 8px 0",
     margin: "16px 0"
   }

   const footerStyle = {
     textAlign: "center",
     padding: "24px",
     fontSize: "0.85rem",
     color: "#9ca3af"
   }

   return (
     <div style={pageStyle}>
       <div style={headerStyle}>
         <h1 style={{ fontSize: "2rem", fontWeight: 700, marginBottom: "8px" }}>Privacy Policy</h1>
         <p style={{ fontSize: "0.95rem", opacity: 0.8 }}>UBS Field App &mdash; Utility Billing System</p>
       </div>

       <div style={containerStyle}>
         <p><strong>Effective Date:</strong> March 19, 2026</p>
         <p><strong>Last Updated:</strong> March 19, 2026</p>
         <p>
           This Privacy Policy describes how UBS Field App collects,
           uses, and protects your information when you use our mobile application. This App is an
           enterprise utility field management tool designed for authorized personnel only.
         </p>

         <SectionTitle>1. Information We Collect</SectionTitle>

         <SubTitle>1.1 Account and Authentication Data</SubTitle>
         <ul style={ulStyle}>
           <li>Username and login credentials provided by your organization</li>
           <li>Authentication tokens (JWT) for secure session management</li>
           <li>User role and assigned billing period</li>
         </ul>

         <SubTitle>1.2 Work-Related Data</SubTitle>
         <ul style={ulStyle}>
           <li>Billing records, meter readings, and payment information entered during field operations</li>
           <li>Task assignments, completion status, and work notes (Technician role)</li>
           <li>Motor reports, fuel readings, and engine condition data (Motor Monitor role)</li>
           <li>Voucher and transaction records</li>
           <li>Customer contact information as provided by the utility organization</li>
         </ul>

         <SubTitle>1.3 Device and Technical Data</SubTitle>
         <ul style={ulStyle}>
           <li>Device type, operating system version, and app version</li>
           <li>Network connectivity status (online/offline detection)</li>
           <li>Locally cached data for offline functionality (when enabled by administrator)</li>
         </ul>

         <SubTitle>1.4 Location Data</SubTitle>
         <ul style={ulStyle}>
           <li>GPS coordinates may be collected when submitting field reports or task completions, solely to verify field
 operations</li>
           <li>Location data is only collected during active use and with device permission</li>
         </ul>

         <SectionTitle>2. How We Use Your Information</SectionTitle>
         <p>We use the collected information exclusively for the following purposes:</p>
         <ul style={ulStyle}>
           <li><strong>Authentication:</strong> To verify your identity and grant role-based access</li>
           <li><strong>Field Operations:</strong> To facilitate billing, meter reading, payment processing, task management, and
 motor monitoring</li>
           <li><strong>Offline Support:</strong> To cache data locally on your device so you can work without internet
 connectivity</li>
           <li><strong>Data Synchronization:</strong> To sync locally stored data with the server when connectivity is
 restored</li>
           <li><strong>App Updates:</strong> To check minimum version requirements and ensure you are using a supported
 version</li>
         </ul>

         <div style={highlightStyle}>
           <strong>Important:</strong> This App is an enterprise tool. All work-related data belongs to and is
           managed by your employing utility organization. We do not use your data for advertising,
           marketing, or any purpose unrelated to field operations.
         </div>

         <SectionTitle>3. Data Storage and Security</SectionTitle>
         <ul style={ulStyle}>
           <li>Authentication tokens are stored securely on your device using platform-secure storage</li>
           <li>Offline data is stored in an encrypted local database on your device</li>
           <li>All network communication uses HTTPS encryption</li>
           <li>Data is transmitted to and stored on servers managed by your utility organization</li>
           <li>Sync queue entries are automatically removed after successful synchronization</li>
           <li>Failed sync items are moved to a dead-letter queue after multiple retry attempts for administrator review</li>
         </ul>

         <SectionTitle>4. Data Sharing</SectionTitle>
         <p>We do <strong>not</strong> sell, trade, or share your personal data with third parties. Data is only shared with:</p>
         <ul style={ulStyle}>
           <li><strong>Your Employer:</strong> All operational data is transmitted to your organization servers as part of normal
  business operations</li>
           <li><strong>No Third-Party Analytics:</strong> We do not integrate third-party analytics, advertising, or tracking
 services</li>
         </ul>

         <SectionTitle>5. Data Retention</SectionTitle>
         <ul style={ulStyle}>
           <li>Local cached data is retained on your device until you log out or the app is uninstalled</li>
           <li>Server-side data retention is governed by your organization data retention policies</li>
           <li>Authentication tokens are invalidated upon logout or session expiry</li>
         </ul>

         <SectionTitle>6. Your Rights</SectionTitle>
         <p>As an authorized user, you have the right to:</p>
         <ul style={ulStyle}>
           <li>Access the data associated with your account through the App</li>
           <li>Log out and clear your local session data at any time</li>
           <li>Request data access or deletion through your organization administrator</li>
           <li>Revoke device permissions (location, network) through your device settings</li>
         </ul>

         <SectionTitle>7. Permissions Used</SectionTitle>
         <p>The App may request the following device permissions:</p>
         <ul style={ulStyle}>
           <li><strong>Internet Access:</strong> Required for data synchronization and authentication</li>
           <li><strong>Network State:</strong> To detect online/offline status for offline mode</li>
           <li><strong>Location (optional):</strong> For GPS tagging of field reports and task completions</li>
           <li><strong>Camera (optional):</strong> For capturing photos related to field tasks</li>
         </ul>

         <SectionTitle>8. Children Privacy</SectionTitle>
         <p>
           This App is an enterprise tool intended for authorized employees only. It is not directed at
           children under the age of 13, and we do not knowingly collect data from children.
         </p>

         <SectionTitle>9. Changes to This Policy</SectionTitle>
         <p>
           We may update this Privacy Policy from time to time. Changes will be reflected by updating
           the Last Updated date at the top of this page. Continued use of the App after changes
           constitutes acceptance of the updated policy.
         </p>

         <SectionTitle>10. Contact Us</SectionTitle>
         <p>If you have any questions about this Privacy Policy or our data practices, please contact:</p>
         <ul style={ulStyle}>
           <li><strong>Email:</strong> support@ubsfield.com</li>
           <li><strong>Organization:</strong> Contact your utility organization administrator</li>
         </ul>
       </div>

       <div style={footerStyle}>
         &copy; 2026 UBS Field App. All rights reserved.
       </div>
     </div>
   )
 }

 const SectionTitle = ({ children }) => (
   <h2 style={{ fontSize: "1.25rem", color: "#111827", margin: "32px 0 12px", paddingBottom: "8px", borderBottom: "2px solid #eef0f6" }}>
     {children}
   </h2>
 )

 const SubTitle = ({ children }) => (
   <h3 style={{ fontSize: "1.05rem", color: "#374151", margin: "20px 0 8px" }}>
     {children}
   </h3>
 )

 const ulStyle = {
   paddingLeft: "24px",
   marginBottom: "12px",
   fontSize: "0.95rem",
   color: "#4b5563",
   lineHeight: 1.7
 }

 export default PrivacyPolicy